import React from "react";
import { motion } from "framer-motion";
import Loader from "../Live/Loader";
import { useAuthValidation } from "../../hooks/useAuthValidation";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../utils/utils";

const CourseDetailsModal = ({ course, isOpen, onClose, loading }) => {
  const { isValid, loading: authValidationLoader, user } = useAuthValidation();
  const navigate = useNavigate();

  // Prevent scrolling when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleEnrollNow = () => {
    try {
      if (course?.requires_placement_test) {
        if (!course?.placement_test?.id) {
          showToast("Unavailable", "A placement test is required for this course but is not set up yet. Please contact support.", "info");
          return;
        }
        navigate(`/placement-tests/${course.placement_test.id}`);
        return;
      }
      if (course?.placement_test?.id) {
        navigate(`/placement-tests/${course.placement_test.id}`);
        return;
      }
      if (isValid) {
        navigate(`/dashboard/courses/details/${course.id}`);
      } else {
        showToast("Login Required", "Please login to enroll in this course.", "info");
        navigate("/login", { state: { redirect: `/dashboard/courses/details/${course.id}` } });
      }
    } catch (error) {
      console.error("Error enrolling in course:", error);
      showToast("Error", "An error occurred while enrolling in the course.", "error");
    }
  };

  // Handle click outside to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 50, scale: 0.95 },
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={handleBackdropClick}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader />
          </div>
        ) : (
          <>
            {/* Modal Header */}
            <div className="relative">
              {course.image_url && (
                <div className="h-[28rem] overflow-hidden">
                  <img
                    src={course.image_url}
                    alt={course.title}
                    className="w-full h-full object-fill"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-70"></div>
                </div>
              )}

              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Title overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h2 className="text-3xl font-bold">{course.title}</h2>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  {course.category && (
                    <span className="bg-primary bg-opacity-80 px-3 py-1 rounded-full text-sm">
                      {course.category.title}
                    </span>
                  )}
                  <span className="flex items-center text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {course.duration}
                  </span>
                  <span className="flex items-center text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    {course.level}
                  </span>
                  <span className="flex items-center text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    {course.instructor_name}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Body - Made scrollable */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="md:col-span-2">
                  {/* Course Description */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-3">
                      About This Course
                    </h3>
                    <p className="text-gray-700">{course.description}</p>
                  </div>

                  {/* Course Lessons - Made scrollable */}
                  {course.lessons && course.lessons.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xl font-bold mb-3">Course Lessons</h3>
                      <div className="space-y-3">
                        <div className="border border-gray-200 rounded-md">
                          <div className="bg-gray-50 px-4 py-2 font-medium">
                            Course Content
                          </div>
                          <div className="p-3 max-h-[300px] overflow-y-auto">
                            <ul className="space-y-1">
                              {course.lessons.map((lesson) => (
                                <li
                                  key={lesson.id}
                                  className={`flex items-start ${
                                    lesson.status !== "active"
                                      ? "opacity-60"
                                      : ""
                                  }`}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-5 w-5 mr-2 flex-shrink-0 ${
                                      lesson.status === "active"
                                        ? "text-primary"
                                        : "text-gray-400"
                                    }`}
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    {lesson.status === "active" ? (
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                      />
                                    ) : (
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 9a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                      />
                                    )}
                                  </svg>
                                  <div>
                                    <div className="flex items-center">
                                      <span className="font-medium">
                                        {lesson.title}
                                      </span>
                                      {lesson.status !== "active" && (
                                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                                          Disabled
                                        </span>
                                      )}
                                    </div>
                                    {lesson.description && (
                                      <p className="text-sm text-gray-600 mt-1">
                                        {lesson.description}
                                      </p>
                                    )}
                                    <div className="text-xs text-gray-500 mt-1">
                                      Duration: {lesson.duration_in_minutes}{" "}
                                      minutes
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Instructor Information */}
                  {course.instructor && (
                    <div className="mb-6">
                      <h3 className="text-xl font-bold mb-3">
                        About the Instructor
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-lg">
                          {course.instructor.first_name}{" "}
                          {course.instructor.last_name}
                        </h4>
                        <p className="text-gray-600 text-sm mt-1">
                          {course.instructor.specialization}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="md:col-span-1">
                  <div className="bg-gray-50 p-4 rounded-lg sticky top-4">
                    <div className="text-2xl font-bold text-primary mb-4">
                      ${course.price}
                    </div>

                    {/* Placement test gate notice */}
                    {course?.placement_test && (
                      <div className="mb-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-xs text-indigo-700">
                        <p className="font-semibold mb-0.5">🎯 Placement Test Required</p>
                        <p className="opacity-80">Complete a placement test before enrolling in this course.</p>
                      </div>
                    )}
                    <button
                      className={`w-full py-2 px-4 text-white rounded-md transition duration-300 mb-4 ${
                        course?.placement_test
                          ? "bg-indigo-600 hover:bg-indigo-700"
                          : "bg-primary hover:bg-primary-dark"
                      }`}
                      onClick={handleEnrollNow}
                    >
                      {authValidationLoader ? <Loader /> : (course?.placement_test ? "Take Placement Test" : "Enroll Now")}
                    </button>

                    <div className="space-y-3 mt-4">
                      <h4 className="font-medium">This course includes:</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2 text-green-500 flex-shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>{course.duration} of content</span>
                        </li>
                        <li className="flex items-start text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2 text-green-500 flex-shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>Access on all devices</span>
                        </li>
                        <li className="flex items-start text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2 text-green-500 flex-shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>Certificate of completion</span>
                        </li>
                        <li className="flex items-start text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2 text-green-500 flex-shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>
                            Limited capacity: {course.capacity} students
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default CourseDetailsModal;
